/**
 * App.jsx
 *
 * HemaAI — Main Application Entry Point
 * Routing hardened to the OmniAlera World-Class Platform Standard, Section 5:
 * every navigation target resolves to a real page, every section defines a
 * fallback so unknown sub-paths land on that section (not a 404), and tier/role
 * guards are enforced in the interface (mirrored server-side).
 *
 * @author Dr. Omokayode Aderolu
 * @company OmniAlera Technologies Holdings Inc.
 * @version 2.1.0
 */

import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Contexts
import { AuthProvider, useAuth } from '@/contexts/AuthContext';

// i18n
import '@/lib/i18n/i18n';

// Layout
import { Layout } from '@/components/layout';

// PWA
import { registerServiceWorker, setupInstallPrompt } from '@/lib/pwa/pwa-service';

// Loading Spinner
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-red-700 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
      <p className="text-gray-600">Loading HemaAI...</p>
    </div>
  </div>
);

// Lazy-loaded pages
const HomePage = lazy(() => import('@/pages/HomePage'));
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/auth/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'));
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const CBCAnalyzerPage = lazy(() => import('@/pages/analyzers/CBCAnalyzerPage'));
const CMPAnalyzerPage = lazy(() => import('@/pages/analyzers/CMPAnalyzerPage'));
const CoagulationAnalyzerPage = lazy(() => import('@/pages/analyzers/CoagulationAnalyzerPage'));
const AnemiaWorkupPage = lazy(() => import('@/pages/analyzers/AnemiaWorkupPage'));
const SmearReviewPage = lazy(() => import('@/pages/analyzers/SmearReviewPage'));
const HemolysisPage = lazy(() => import('@/pages/analyzers/HemolysisPage'));
const UnitConverterPage = lazy(() => import('@/pages/tools/UnitConverterPage'));
const CalculatorsPage = lazy(() => import('@/pages/calculators/CalculatorsPage'));
const TrendsPage = lazy(() => import('@/pages/TrendsPage'));
const AnalyzersLandingPage = lazy(() => import('@/pages/analyzers/AnalyzersLandingPage'));
const AboutFounderPage = lazy(() => import('@/pages/AboutFounderPage'));
const PricingPage = lazy(() => import('@/pages/PricingPage'));
const TelemedicinePage = lazy(() => import('@/pages/TelemedicinePage'));
const VRSimulationPage = lazy(() => import('@/pages/VRSimulationPage'));
const PhysicianDashboardPage = lazy(() => import('@/pages/physician/PhysicianDashboardPage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const InteroperabilityPage = lazy(() => import('@/pages/InteroperabilityPage'));
const AdminSystemPage = lazy(() => import('@/pages/AdminSystemPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

// Legal Pages
const PrivacyPolicyPage = lazy(() => import('@/pages/legal/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('@/pages/legal/TermsOfServicePage'));
const HIPAACompliancePage = lazy(() => import('@/pages/legal/HIPAACompliancePage'));
const MedicalDisclaimerPage = lazy(() => import('@/pages/legal/MedicalDisclaimerPage'));
const ContactPage = lazy(() => import('@/pages/legal/ContactPage'));
const FAQPage = lazy(() => import('@/pages/legal/FAQPage'));

// React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      cacheTime: 1000 * 60 * 30,
      retry: 1,
      refetchOnWindowFocus: false
    }
  }
});

/**
 * Protected Route Component — enforces auth, role, and tier in the UI.
 * The API independently enforces the same role/tier for privileged actions.
 */
const ProtectedRoute = ({ children, requiredRole, requiredTier }) => {
  const { isAuthenticated, isLoading, hasRole, hasTier } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requiredRole && !hasRole(requiredRole)) {
    return <Navigate to="/dashboard" replace />;
  }

  if (requiredTier && !hasTier(requiredTier)) {
    return <Navigate to="/pricing" replace />;
  }

  return children;
};

/**
 * Public Route (redirects if authenticated)
 */
const PublicRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

/**
 * Main App Component
 */
const App = () => {
  useEffect(() => {
    registerServiceWorker();
    setupInstallPrompt();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              {/* Public Routes with Layout */}
              <Route element={<Layout />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/about-founder" element={<AboutFounderPage />} />
                <Route path="/pricing" element={<PricingPage />} />

                {/* Analyzers — LOGIN-GATED pending clinical validation.
                    Section index + explicit pages + section fallback. */}
                <Route path="/analyzers" element={<ProtectedRoute><AnalyzersLandingPage /></ProtectedRoute>} />
                <Route path="/analyzers/cbc" element={<ProtectedRoute><CBCAnalyzerPage /></ProtectedRoute>} />
                <Route path="/analyzers/cmp" element={<ProtectedRoute><CMPAnalyzerPage /></ProtectedRoute>} />
                <Route path="/analyzers/coagulation" element={<ProtectedRoute><CoagulationAnalyzerPage /></ProtectedRoute>} />
                <Route path="/analyzers/anemia" element={<ProtectedRoute><AnemiaWorkupPage /></ProtectedRoute>} />
                <Route path="/analyzers/smear" element={<ProtectedRoute><SmearReviewPage /></ProtectedRoute>} />
                <Route path="/analyzers/hemolysis" element={<ProtectedRoute><HemolysisPage /></ProtectedRoute>} />
                {/* Unknown /analyzers/* lands on the section (also gated) */}
                <Route path="/analyzers/*" element={<ProtectedRoute><AnalyzersLandingPage /></ProtectedRoute>} />

                {/* Clinical calculators — login-gated */}
                <Route path="/calculators" element={<ProtectedRoute><CalculatorsPage /></ProtectedRoute>} />
                <Route path="/calculators/*" element={<ProtectedRoute><CalculatorsPage /></ProtectedRoute>} />

                {/* Longitudinal trends — login-gated */}
                <Route path="/trends" element={<ProtectedRoute><TrendsPage /></ProtectedRoute>} />
                <Route path="/trends/*" element={<ProtectedRoute><TrendsPage /></ProtectedRoute>} />

                {/* Tools — login-gated */}
                <Route path="/tools/converter" element={<ProtectedRoute><UnitConverterPage /></ProtectedRoute>} />
                <Route path="/tools/*" element={<ProtectedRoute><UnitConverterPage /></ProtectedRoute>} />

                {/* Legal Pages */}
                <Route path="/privacy" element={<PrivacyPolicyPage />} />
                <Route path="/terms" element={<TermsOfServicePage />} />
                <Route path="/hipaa" element={<HIPAACompliancePage />} />
                <Route path="/medical-disclaimer" element={<MedicalDisclaimerPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/faq" element={<FAQPage />} />
                <Route path="/help" element={<FAQPage />} />
                {/* Unknown /legal/* lands on the FAQ/help hub, not a 404 */}
                <Route path="/legal/*" element={<FAQPage />} />
              </Route>

              {/* VR Simulation - No standard layout — login-gated */}
              <Route path="/vr-simulation" element={<ProtectedRoute><VRSimulationPage /></ProtectedRoute>} />

              {/* Auth Routes */}
              <Route path="/login" element={
                <PublicRoute><LoginPage /></PublicRoute>
              } />
              <Route path="/register" element={
                <PublicRoute><RegisterPage /></PublicRoute>
              } />
              <Route path="/forgot-password" element={
                <PublicRoute><ForgotPasswordPage /></PublicRoute>
              } />

              {/* Protected Routes */}
              <Route element={<Layout />}>
                <Route path="/dashboard" element={
                  <ProtectedRoute><DashboardPage /></ProtectedRoute>
                } />
                <Route path="/settings" element={
                  <ProtectedRoute><SettingsPage /></ProtectedRoute>
                } />
                <Route path="/telemedicine" element={
                  <ProtectedRoute><TelemedicinePage /></ProtectedRoute>
                } />
                <Route path="/interoperability" element={
                  <ProtectedRoute><InteroperabilityPage /></ProtectedRoute>
                } />
                <Route path="/physician" element={
                  <ProtectedRoute requiredRole="PHYSICIAN"><PhysicianDashboardPage /></ProtectedRoute>
                } />
                {/* Unknown /physician/* lands on the physician dashboard */}
                <Route path="/physician/*" element={
                  <ProtectedRoute requiredRole="PHYSICIAN"><PhysicianDashboardPage /></ProtectedRoute>
                } />
                <Route path="/admin" element={
                  <ProtectedRoute requiredRole="COMPLIANCE_OFFICER"><AdminSystemPage /></ProtectedRoute>
                } />
                <Route path="/admin/*" element={
                  <ProtectedRoute requiredRole="COMPLIANCE_OFFICER"><AdminSystemPage /></ProtectedRoute>
                } />
              </Route>

              {/* Global 404 fallback */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
